<!-- src/components/common/BatteryPanelClient.vue -->
<!--
  Onglet Batterie de l'ESPACE CLIENT (02/10/2026). L'admin garde BatteryPanel.vue, complet
  (courbes en mV, seuils, vocabulaire de la jauge) : c'est un outil de diagnostic.

  Le client, lui, lit des % et des mA·h, jamais des mV (cahier §5), et aucun terme de firmware
  (BATFET, coulombs, PPK2, dq) ni projection en jours — « entrée en éco dans ~N j » était une
  autonomie déguisée. Les badges sont traduits d'après le NIVEAU renvoyé par le serveur, jamais
  d'après son texte français, qui reste rédigé pour l'admin.

  Même découpage que l'admin : relevés horaires jusqu'à 7 jours, résumé par jour au-delà
  (`pct_min`/`pct_max`, `cap_min`/`cap_max`/`cap_last` de battery_daily).
-->
<template>
  <div class="bg-white rounded-lg shadow p-5">
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h3 class="font-semibold text-gray-800">{{ $t('batterieClient.titre') }}</h3>
      <div class="flex flex-wrap gap-2">
        <span v-for="b in badges" :key="b.label"
              :class="`px-3 py-1 rounded-full text-sm font-semibold ${b.cls}`">{{ b.label }}</span>
      </div>
    </div>
    <p v-for="b in badges" :key="'d' + b.label" class="text-xs text-gray-500 mb-2">{{ b.detail }}</p>
    <p v-if="consoLigne" class="text-xs text-gray-600 mb-1">{{ consoLigne }}</p>
    <p v-if="consoLigne" class="text-xs text-gray-400 mb-3">{{ $t('batterieClient.aideConso') }}</p>

    <!-- Contrôles : unité, plage, navigation -->
    <div class="flex flex-wrap items-center gap-2 mb-3">
      <div class="inline-flex rounded-md overflow-hidden border border-gray-200">
        <button v-for="r in plages" :key="r.d" type="button" @click="plage = r.d"
                :class="['px-3 py-1 text-sm', plage === r.d ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50']">
          {{ r.label }}
        </button>
      </div>
      <div class="inline-flex items-center gap-1 ml-1">
        <button type="button" @click="decaler(-1)" :aria-label="$t('batterieClient.precedent')"
                class="px-2 py-1 text-sm rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">←</button>
        <button type="button" @click="decaler(1)" :disabled="!peutAvancer" :aria-label="$t('batterieClient.suivant')"
                :class="['px-2 py-1 text-sm rounded border border-gray-200', peutAvancer ? 'bg-white text-gray-600 hover:bg-gray-50' : 'bg-gray-50 text-gray-300 cursor-not-allowed']">→</button>
      </div>
      <span class="text-xs text-gray-500">{{ fenetreLabel }}</span>
    </div>

    <div v-if="chargement" class="py-10 text-center text-sm text-gray-400">{{ $t('commun.chargement') }}</div>
    <template v-else>
      <div v-for="g in graphes" :key="g.unite" class="w-full mt-2">
        <div class="text-xs font-medium text-gray-600 mb-0.5">{{ g.titre }}</div>
        <svg v-if="!g.vide" :viewBox="`0 0 ${W} ${g.H}`" class="w-full" preserveAspectRatio="xMidYMid meet" role="img"
             :aria-label="g.aria">
          <!-- Bandes min → max (vue par jour) -->
          <g v-for="(b, i) in g.bandes" :key="'b' + i">
            <rect :x="b.x - b.w / 2" :y="b.yHaut" :width="b.w" :height="Math.max(1, b.yBas - b.yHaut)"
                  :rx="Math.min(2, b.w / 2)" :fill="g.couleur" opacity="0.25"><title>{{ b.tip }}</title></rect>
          </g>
          <polyline v-for="(seg, i) in g.lignes" :key="'l' + i" :points="seg" fill="none"
                    :stroke="g.couleur" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round" />
          <g v-if="g.points.length <= 200">
            <circle v-for="(p, i) in g.points" :key="'p' + i" :cx="p.x" :cy="p.y" r="2" :fill="p.couleur">
              <title>{{ p.tip }}</title>
            </circle>
          </g>
          <g v-for="tk in g.ticksY" :key="'y' + tk.v">
            <line :x1="padL" :x2="W - padR" :y1="tk.y" :y2="tk.y" stroke="#f3f4f6" stroke-width="1" />
            <text :x="padL - 6" :y="tk.y + 3" text-anchor="end" fill="#6b7280" font-size="10">{{ tk.v }}</text>
          </g>
          <g v-for="(lbl, i) in g.ticksX" :key="'x' + i">
            <text :x="lbl.x" :y="g.H - 6" text-anchor="middle" fill="#6b7280" font-size="10">{{ lbl.text }}</text>
          </g>
        </svg>
        <div v-else class="py-6 text-center text-sm text-gray-400">{{ $t('batterieClient.aucuneMesure') }}</div>
      </div>
      <div v-if="vueHoraire" class="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
        <span v-for="r in regimes" :key="r.cle" class="inline-flex items-center gap-1">
          <span class="w-2.5 h-2.5 rounded-full inline-block" :style="{ background: r.couleur }"></span>{{ r.label }}
        </span>
      </div>
    </template>

    <p v-if="capDisponible" class="text-xs text-gray-400 mt-3">{{ $t('batterieClient.aideCap') }}</p>
    <p class="text-xs text-gray-400 mt-2">{{ $t('batterieClient.aideSolaire') }}</p>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import apiService from '@/services/api.service';
import { localeIntl, FUSEAU } from '@/i18n/dates';

const JOUR = 86400;
const TROU_MAX = 3 * 3600;   // même règle que l'admin : on ne relie pas deux points séparés par un silence
const COULEURS = { solar: '#f59e0b', mains: '#3b82f6', battery: '#8b5cf6' };

export default {
  name: 'BatteryPanelClient',
  props: { deviceId: { type: Number, required: true } },
  setup(props) {
    const { t } = useI18n();
    const plage = ref(1);
    const maintenant = () => Math.floor(Date.now() / 1000);
    const fin = ref(maintenant());
    const debut = computed(() => fin.value - plage.value * JOUR);
    const vueHoraire = computed(() => plage.value <= 7);
    const peutAvancer = computed(() => fin.value < maintenant() - 60);

    const jours = ref([]);
    const releves = ref([]);
    const sante = ref(null);
    const conso = ref(null);
    const bilan = ref(null);
    const chargement = ref(true);

    const chargerJours = async () => {
      const { data } = await apiService.getDeviceBattery(props.deviceId, 30);
      jours.value = data.daily || [];
      sante.value = data.health || null;
      conso.value = data.consumption || null;
      bilan.value = data.balance || null;
    };
    const chargerReleves = async () => {
      const { data } = await apiService.getDeviceBatterySamples(props.deviceId, debut.value, fin.value);
      releves.value = data.samples || [];
    };
    const rafraichir = async () => {
      chargement.value = true;
      try { if (vueHoraire.value) await chargerReleves(); }
      catch (e) { console.error('[BATT] client :', e); }
      finally { chargement.value = false; }
    };
    onMounted(async () => {
      try { await chargerJours(); if (vueHoraire.value) await chargerReleves(); }
      catch (e) { console.error('[BATT] client :', e); }
      finally { chargement.value = false; }
    });
    watch([plage, fin], rafraichir);
    const decaler = (sens) => { fin.value = Math.min(fin.value + sens * plage.value * JOUR, maintenant()); };

    const plages = computed(() => [
      { d: 1, label: t('batterie.plageJour') }, { d: 3, label: t('batterie.plageJours', { n: 3 }) },
      { d: 7, label: t('batterie.plageJours', { n: 7 }) }, { d: 30, label: t('batterie.plageJours', { n: 30 }) }
    ]);
    // La capacité n'existe que sur les cartes dont la jauge la mesure : sans elle, pas de choix.
    const capDisponible = computed(() => jours.value.some(j => j.cap_last > 0)
      || releves.value.some(r => r.cap > 0));
    const regimes = computed(() => [
      { cle: 'solar', couleur: COULEURS.solar, label: t('batterie.solaire') },
      { cle: 'mains', couleur: COULEURS.mains, label: t('batterie.secteur') },
      { cle: 'battery', couleur: COULEURS.battery, label: t('batterie.batterie') }
    ]);

    // ---- Badges : traduits d'après le NIVEAU, jamais d'après le texte français du serveur ----
    const badges = computed(() => {
      const out = [];
      const h = sante.value;
      const NIV = { dead_panel: ['PanneauVerifier', 'bg-red-100 text-red-700'], seasonal: ['RecolteBaisse', 'bg-amber-100 text-amber-800'],
                    eco: ['ModeEco', 'bg-red-100 text-red-700'], full: ['Pleine', 'bg-green-100 text-green-700'],
                    ok: ['Equilibre', 'bg-green-100 text-green-700'] };
      if (h && NIV[h.level]) {
        const [cle, cls] = NIV[h.level];
        const p = h.params || {};
        out.push({ cls, label: t(`batterieClient.sante${cle}`), detail: t(`batterieClient.sante${cle}Detail`, { jours: p.jours ?? '?' }) });
      }
      const b = bilan.value;
      if (b && b.level && b.level !== 'n/a') {
        const solaire = b.regime === 'solar';
        if (b.level === 'no_charge') {
          out.push({ cls: 'bg-red-100 text-red-700', label: t('batterieClient.rechargeNulle'),
                     detail: t(solaire ? 'batterieClient.rechargeNulleSolaire' : 'batterieClient.rechargeNulleSecteur', { jours: b.days }) });
        } else if (b.level === 'unknown' && b.reason === 'dq_zero') {
          out.push({ cls: 'bg-amber-100 text-amber-800', label: t('batterieClient.mesureIndisponible'), detail: t('batterieClient.mesureIndisponibleDetail') });
        } else if (b.level === 'unknown') {
          out.push({ cls: 'bg-gray-100 text-gray-600', label: t('batterieClient.rechargeAConfirmer'), detail: t('batterieClient.rechargeAConfirmerDetail', { jours: b.days }) });
        } else {
          out.push({ cls: 'bg-green-100 text-green-700', label: t('batterieClient.rechargeActive'),
                     detail: t('batterieClient.rechargeActiveDetail', { enCharge: b.negatives, mesures: b.samples, jours: b.days }) });
        }
      }
      const d = conso.value && conso.value.drift;
      if (d && d.level === 'drift') {
        out.push({ cls: 'bg-red-100 text-red-700', label: t('batterieClient.consoHausse', { facteur: d.factor }),
                   detail: t('batterieClient.consoHausseDetail', { recent: d.recent, jours: d.recent_days, base: d.baseline }) });
      }
      return out;
    });

    const consoLigne = computed(() => {
      const c = conso.value;
      if (!c || c.reason === 'no_data') return null;
      const suffixe = c.cap != null ? t('batterieClient.capaciteRestante', { cap: c.cap }) : '';
      if (c.mah_per_day != null) return t('batterieClient.consoMesuree', { mah: c.mah_per_day, jours: c.days }) + suffixe;
      if (c.reason === 'on_mains') return t('batterieClient.consoSecteur') + suffixe;
      if (c.reason === 'cadence') return t('batterieClient.consoCadence') + suffixe;
      return t('batterieClient.consoEnCours', { jours: c.days, fenetre: c.window_days }) + suffixe;
    });

    // ---- Graphe ----
    const W = 720, padL = 44, padR = 16, padT = 14, padB = 22;
    const fmtHeure = ts => new Date(ts * 1000).toLocaleString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    const fmtJour = j => new Date(`${j}T12:00:00Z`).toLocaleDateString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit' });

    // Deux courbes, l'une sous l'autre, même axe de temps : le % (ce que le client lit d'abord)
    // et la capacité restante en mA·h, quand la carte la mesure. Échelles séparées : les deux
    // n'ont pas d'unité commune, les superposer ferait lire des croisements qui ne veulent rien dire.
    const grapheDe = (unite, H) => {
      const enPct = unite === 'pct';
      const xOf = ts => padL + (ts - debut.value) / (fin.value - debut.value) * (W - padL - padR);
      // Points (horaire) ou bandes (par jour) dans l'unité choisie. Une mesure absente n'est
      // jamais un zéro : elle n'est pas tracée.
      let points = [], bandes = [];
      if (vueHoraire.value) {
        points = releves.value
          .map(r => ({ ts: r.ts, v: enPct ? r.bat_pct : (r.cap > 0 ? r.cap : null), src: r.src }))
          .filter(p => p.v != null);
      } else {
        bandes = jours.value.map(j => {
          const ts = Date.parse(`${j.day}T12:00:00Z`) / 1000;
          const bas = enPct ? j.pct_min : j.cap_min, haut = enPct ? j.pct_max : j.cap_max;
          const ligne = enPct ? j.pct_max : j.cap_last;
          return { ts, bas, haut, ligne, day: j.day };
        }).filter(b => b.ts >= debut.value && b.ts <= fin.value && b.haut != null);
      }
      const valeurs = vueHoraire.value ? points.map(p => p.v) : bandes.flatMap(b => [b.bas, b.haut, b.ligne]).filter(v => v != null);
      if (!valeurs.length) return { vide: true, H };

      // Échelle : 0–100 % fixe (un % se lit sur son échelle entière) ; mA·h calé sur les données,
      // avec une amplitude plancher pour ne pas transformer le pas de la jauge en montagnes russes.
      let lo = 0, hi = 100;
      if (!enPct) {
        const mn = Math.min(...valeurs), mx = Math.max(...valeurs);
        const milieu = (mn + mx) / 2, ampl = Math.max(mx - mn, 30) * 1.15;
        lo = Math.max(0, Math.round(milieu - ampl / 2)); hi = Math.round(milieu + ampl / 2);
      }
      const yOf = v => padT + (hi - Math.max(lo, Math.min(hi, v))) / (hi - lo) * (H - padT - padB);
      const unit = enPct ? ' %' : ' mA·h';

      const lignes = [];
      const pts = [];
      if (vueHoraire.value) {
        let cur = [];
        points.forEach((p, i) => {
          if (i > 0 && p.ts - points[i - 1].ts > TROU_MAX) { if (cur.length > 1) lignes.push(cur.join(' ')); cur = []; }
          cur.push(`${xOf(p.ts).toFixed(1)},${yOf(p.v).toFixed(1)}`);
          pts.push({ x: xOf(p.ts), y: yOf(p.v), couleur: COULEURS[p.src] || '#9ca3af', tip: `${fmtHeure(p.ts)} — ${p.v}${unit}` });
        });
        if (cur.length > 1) lignes.push(cur.join(' '));
      } else {
        const largeur = Math.max(3, (W - padL - padR) / Math.max(plage.value, 1) * 0.6);
        const ligne = [];
        for (const b of bandes) {
          const x = xOf(b.ts);
          if (b.bas != null && b.haut != null) {
            b.rect = { x, w: largeur, yHaut: yOf(b.haut), yBas: yOf(b.bas),
              tip: `${fmtJour(b.day)} — ${t('batterieClient.minMax', { min: b.bas, max: b.haut, unite: unit.trim() })}` };
          }
          if (b.ligne != null) { ligne.push(`${x.toFixed(1)},${yOf(b.ligne).toFixed(1)}`); pts.push({ x, y: yOf(b.ligne), couleur: enPct ? '#0b2e40' : '#0d9488', tip: `${fmtJour(b.day)} — ${b.ligne}${unit}` }); }
        }
        if (ligne.length > 1) lignes.push(ligne.join(' '));
      }
      const ticksY = (enPct ? [0, 25, 50, 75, 100] : [hi, Math.round((lo + hi) / 2), lo]).map(v => ({ v, y: yOf(v) }));
      const ticksX = Array.from({ length: 7 }, (_, i) => {
        const ts = debut.value + (fin.value - debut.value) * i / 6;
        const d = new Date(ts * 1000);
        const text = plage.value <= 1
          ? d.toLocaleTimeString(localeIntl(), { timeZone: FUSEAU, hour: '2-digit', minute: '2-digit' })
          : d.toLocaleDateString(localeIntl(), { timeZone: FUSEAU, day: '2-digit', month: '2-digit' });
        return { x: xOf(ts), text };
      });
      return { vide: false, H, lignes, points: pts, bandes: bandes.filter(b => b.rect).map(b => b.rect), ticksY, ticksX };
    };
    const graphes = computed(() => {
      const out = [{ ...grapheDe('pct', 220), unite: 'pct', couleur: '#0b2e40',
                     titre: t('batterieClient.courbePct'), aria: t('batterieClient.ariaPct') }];
      if (capDisponible.value) {
        out.push({ ...grapheDe('cap', 150), unite: 'cap', couleur: '#0d9488',
                   titre: t('batterieClient.courbeCap'), aria: t('batterieClient.ariaCap') });
      }
      return out;
    });

    const fenetreLabel = computed(() => `${fmtHeure(debut.value)} → ${fmtHeure(fin.value)}`);

    return { plage, plages, capDisponible, regimes, vueHoraire, peutAvancer, decaler,
             chargement, badges, consoLigne, graphes, fenetreLabel, W, padL, padR };
  }
};
</script>
